import { create } from "zustand";
import {
  createInitialChartSlotFeatures,
  type ChartFeatureFlagKey,
  type ChartFeatureSlotState,
} from "@/Popups/contentSlide/newPrecedentSlide/steps/formattingFeaturesConfig";

export type NewPrecedentWizardAnswersByStep = [number, number, number, number, number];
export type NewPrecedentChartType = "timeSeriesLineChart" | "columnChart";

export type NewPrecedentSlideLayoutId = "layout_101" | "layout_111" | "layout_201" | "layout_211";

export type NewPrecedentChartSlotConfig = {
  chartType: NewPrecedentChartType | null;
  features: ChartFeatureSlotState;
};

export type NewPrecedentChartConfig = {
  chart1: NewPrecedentChartSlotConfig;
  chart2?: NewPrecedentChartSlotConfig;
};

export type BackendPayload = {
  slideLayout1: string;
  chartPlaceholderType1: NewPrecedentChartType | null;
  chartPlaceholderType2: NewPrecedentChartType | null;
  sharePriceCharacteristics: {
    sharePriceType: string;
    currency: string;
  };
  chart1: ChartFeatureSlotState;
  chart2?: ChartFeatureSlotState;
  formatTemplateId: string | null;
};

function emptyChartSlot(): NewPrecedentChartSlotConfig {
  return {
    chartType: null,
    features: createInitialChartSlotFeatures(),
  };
}

export function createChartConfig(chartCount: 1 | 2): NewPrecedentChartConfig {
  return chartCount === 2
    ? { chart1: emptyChartSlot(), chart2: emptyChartSlot() }
    : { chart1: emptyChartSlot() };
}

/** Layouts with a text (key-event) column: `layout_201`. */
export function isKeyEventPrecedentLayout(layoutId: NewPrecedentSlideLayoutId | null): boolean {
  return layoutId === "layout_201";
}

interface NewPrecedentSlideState {
  step: number;
  setStep: (step: number) => void;
  answersByStep: NewPrecedentWizardAnswersByStep;
  setAnswersByStep: (answers: NewPrecedentWizardAnswersByStep) => void;
  selectedLayoutId: NewPrecedentSlideLayoutId | null;
  setSelectedLayoutId: (layoutId: NewPrecedentSlideLayoutId | null) => void;
  isKeyEvent: boolean;
  chartCount: 1 | 2 | null;
  setChartCount: (count: 1 | 2 | null) => void;
  chartConfig: NewPrecedentChartConfig;
  setChartSlotType: (chartIndex: 0 | 1, chartType: NewPrecedentChartType | null) => void;
  setChartFeatureFlag: (chartIndex: 0 | 1, featureKey: ChartFeatureFlagKey, binary: boolean) => void;
  updateChartFeatureUserInput: (
    chartIndex: 0 | 1,
    featureKey: ChartFeatureFlagKey,
    userInput: string
  ) => void;
  getPayload: () => BackendPayload | null;
  sharePriceType: string;
  setSharePriceType: (value: string) => void;
  sharePriceCurrency: string;
  setSharePriceCurrency: (value: string) => void;
  selectedFormatTemplateId: string | null;
  setSelectedFormatTemplateId: (id: string | null) => void;
  reset: () => void;
}

export const useNewPrecedentSlideStore = create<NewPrecedentSlideState>()((set, get) => ({
  step: 0,
  setStep: (step) => set({ step }),
  answersByStep: [0, 0, 0, 0, 0],
  setAnswersByStep: (answersByStep) => set({ answersByStep }),
  selectedLayoutId: null,
  setSelectedLayoutId: (selectedLayoutId) =>
    set({
      selectedLayoutId,
      isKeyEvent: isKeyEventPrecedentLayout(selectedLayoutId),
    }),
  isKeyEvent: false,
  chartCount: null,
  setChartCount: (chartCount) => set({ chartCount }),
  chartConfig: createChartConfig(1),
  setChartSlotType: (chartIndex, chartType) =>
    set((s) => {
      if (chartIndex === 0) {
        const slot = { ...s.chartConfig.chart1, chartType };
        return { chartConfig: { ...s.chartConfig, chart1: slot } };
      }
      if (s.chartConfig.chart2 === undefined) return {};
      const slot = { ...s.chartConfig.chart2, chartType };
      return { chartConfig: { ...s.chartConfig, chart2: slot } };
    }),
  setChartFeatureFlag: (chartIndex, featureKey, binary) =>
    set((s) => {
      const patch = (slot: NewPrecedentChartSlotConfig): NewPrecedentChartSlotConfig => ({
        ...slot,
        features: {
          ...slot.features,
          [featureKey]: {
            binary,
            userInput: binary ? slot.features[featureKey].userInput : "",
          },
        },
      });
      if (chartIndex === 0) {
        return { chartConfig: { ...s.chartConfig, chart1: patch(s.chartConfig.chart1) } };
      }
      if (!s.chartConfig.chart2) return {};
      return { chartConfig: { ...s.chartConfig, chart2: patch(s.chartConfig.chart2) } };
    }),
  updateChartFeatureUserInput: (chartIndex, featureKey, userInput) =>
    set((s) => {
      const patch = (slot: NewPrecedentChartSlotConfig): NewPrecedentChartSlotConfig => ({
        ...slot,
        features: {
          ...slot.features,
          [featureKey]: { ...slot.features[featureKey], userInput },
        },
      });
      if (chartIndex === 0) {
        return { chartConfig: { ...s.chartConfig, chart1: patch(s.chartConfig.chart1) } };
      }
      if (!s.chartConfig.chart2) return {};
      return { chartConfig: { ...s.chartConfig, chart2: patch(s.chartConfig.chart2) } };
    }),
  getPayload: () => {
    const s = get();
    if (!s.selectedLayoutId || !s.chartConfig.chart1.chartType) return null;
    return {
      slideLayout1: s.selectedLayoutId,
      chartPlaceholderType1: s.chartConfig.chart1.chartType,
      chartPlaceholderType2: s.chartConfig.chart2?.chartType ?? null,
      sharePriceCharacteristics: {
        sharePriceType: s.sharePriceType,
        currency: s.sharePriceCurrency,
      },
      chart1: s.chartConfig.chart1.features,
      chart2: s.chartConfig.chart2?.features,
      formatTemplateId: s.selectedFormatTemplateId,
    };
  },
  sharePriceType: "adjusted_closing_share_price",
  setSharePriceType: (sharePriceType) => set({ sharePriceType }),
  sharePriceCurrency: "USD",
  setSharePriceCurrency: (sharePriceCurrency) => set({ sharePriceCurrency }),
  selectedFormatTemplateId: null,
  setSelectedFormatTemplateId: (selectedFormatTemplateId) => set({ selectedFormatTemplateId }),
  reset: () =>
    set({
      step: 0,
      answersByStep: [0, 0, 0, 0, 0],
      selectedLayoutId: null,
      isKeyEvent: false,
      chartCount: null,
      chartConfig: createChartConfig(1),
      sharePriceType: "adjusted_closing_share_price",
      sharePriceCurrency: "USD",
      selectedFormatTemplateId: null,
    }),
}));
